/**
 * Import Service API - Dashboard, Jobs, Configurations, Middleware Status
 */
import api, { type PaginatedResponse } from './client';
import type { MiddlewareStatusResponse } from './middlewareHealth';

// ============================================================================
// Types
// ============================================================================

export interface ImportJob {
  id: number;
  job_id: string;
  correlation_id: string;
  data_type: string;
  data_type_display: string;
  source: string;
  source_display: string;
  is_incremental: boolean;
  customer: number;
  customer_name: string;
  status: string;
  status_display: string;
  total_records: number;
  records_created: number;
  records_updated: number;
  records_skipped: number;
  records_failed: number;
  records_processed: number;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface HealthCheckDescriptor {
  id: string;
  name: string;
  description: string;
}

export type HealthCheckStatus = 'pass' | 'warning' | 'fail' | 'error';

export interface HealthCheckResponse {
  check_id: string;
  customer_id: number;
  status: HealthCheckStatus;
  summary: string;
  metrics: Record<string, unknown>;
  samples: Array<Record<string, unknown>>;
  duration_ms: number;
}

export interface ImportJobDetail extends ImportJob {
  error_traceback: string;
  source_file_name: string;
  celery_task_id: string;
  error_breakdown: Array<{ error_code: string; count: number }>;
  debug_sample_raw: Array<Record<string, unknown>>;
  debug_sample_processed: Array<Record<string, unknown>>;
}

export interface ImportLog {
  id: number;
  action: string;
  action_display: string;
  entity_type: string;
  entity_id: number | null;
  entity_reference: string;
  source_row: number | null;
  source_key: string;
  fields_changed: Record<string, unknown>;
  error_code: string;
  error_message: string;
  created_at: string;
}

export interface ImportConfiguration {
  id: number;
  customer: number;
  customer_name: string;
  data_type: string;
  data_type_display: string;
  schedule_type: string;
  schedule_type_display: string;
  cron_expression: string;
  interval_minutes: number | null;
  is_incremental: boolean;
  batch_size: number;
  max_retries: number;
  retry_delay_seconds: number;
  source_config: Record<string, unknown>;
  field_mappings: Record<string, unknown>;
  auto_create_dependencies: boolean;
  validate_before_import: boolean;
  is_active: boolean;
  last_run_at: string | null;
  last_success_at: string | null;
  consecutive_failures: number;
  created_at: string;
  updated_at: string;
}

export interface DashboardSummary {
  total_jobs: number;
  jobs_today: number;
  jobs_running: number;
  jobs_failed_today: number;
  records_created_today: number;
  records_updated_today: number;
  records_skipped_today: number;
  records_failed_today: number;
  by_data_type: Array<{
    data_type: string;
    total: number;
    succeeded: number;
    failed: number;
    records_created: number;
    records_failed: number;
  }>;
  recent_failures: ImportJob[];
}

export type {
  MiddlewareQueue,
  MiddlewareJobState,
  MiddlewareHealthSource,
  MiddlewareHealthEvent,
  MiddlewareHealth,
  MiddlewareHealthReport,
  MiddlewareStatusResponse,
} from './middlewareHealth';

/**
 * ERP schedule data-age (Avanti tenants). The ERP only rewrites schedule
 * content at its weekly regeneration, so the portal's schedule photograph
 * can be days old while pushes complete green — this makes that visible.
 */
export interface DataAgeChange {
  type: 'added' | 'removed' | 'reactivated' | 'qty_changed';
  po_number: string;
  line_number: number | null;
  part_number: string;
  schedule_date: string | null;
  qty: string | null;
  /** Populated for qty_changed rows: the ERP re-planned old -> new. */
  old_qty: string | null;
  new_qty: string | null;
  at: string;
}

export interface DataAgeResponse {
  enabled: boolean;
  customer_id?: number;
  customer_name?: string;
  /** When pushed schedule content last actually differed from the portal. */
  last_replan_at?: string | null;
  /** Schedule lines added/removed/reactivated on that day. */
  replan_changes_on_last_day?: number;
  /** One page of the merged change stream (newest first). */
  changes?: DataAgeChange[];
  changes_total?: number;
  page?: number;
  page_size?: number;
  changes_truncated?: boolean;
  /** Latest PO push processed (job-level fallback). */
  last_po_push_at?: string | null;
  /** Whole-push state from START/END marker bookkeeping. */
  push_state?: 'complete' | 'in_progress' | 'unknown';
  push_meta?: {
    completed_at?: string | null;
    pages?: number | null;
    rows?: number | null;
    rows_processed?: number | null;
  } | null;
  /** Whether `changes` covers the latest push or a day-bucket fallback. */
  changes_scope?: 'last_push' | 'last_replan_day' | 'none';
  push_started_at?: string | null;
}


/** Import source for switching between P&A, Atlas Copco, Poeton, and Data Import views */
export type ImportSource = 'pna' | 'atlascopco' | 'poeton' | 'data_import';

export interface ImportSourceOption {
  key: string;
  source: ImportSource;
  customer_id: number;
  customer_name: string;
}

/**
 * One writeback at whatever point of its life it is in. The three backing
 * tables (pending / succeeded / failed) share this shape, so the tab renders
 * a single table per outcome rather than three bespoke ones.
 */
export interface WritebackRow {
  id: number;
  po_number: string;
  /** The portal's display position for the line, not the ERP's row key. */
  line_number: string;
  po_line_id: number | null;
  /**
   * The ERP row key the middleware addresses the row by. Diverges from
   * `line_number` once a line is deleted or re-sequenced in the ERP. Null on
   * lines imported before the field existed. Shown only when the platform
   * setting `writeback_show_sap_row` is on.
   */
  sap_order_row: number | null;
  supplier_name: string | null;
  change_type: number;
  change_type_display: string;
  /** null for accept/decline acknowledgements — they carry no before/after. */
  /** DATE-FIELD-CANON PR 3: the API now sends 'customer_required_date'.
   *  'delivery_date' stays in the union for the deprecation window so a
   *  page served against an older backend still renders. */
  field: 'customer_required_date' | 'delivery_date' | 'unit_price' | 'quantity' | null;
  previous_value: string | number | null;
  current_value: string | number | null;
  /** When the portal raised it. */
  raised_at: string | null;
  /** Outcome time: created (pending), completed (succeeded), failed (failed). */
  timestamp: string | null;
  // Pending only
  status?: number;
  status_display?: string;
  try_times?: number;
  last_response?: string;
  // Failed only
  failed_type?: number;
  failed_type_display?: string;
  error?: string;
  // Cancelled only
  cancelled_by?: string | null;
  cancel_reason?: string;
}

export interface WritebackStatusResponse {
  customer_id: number;
  window_hours: number;
  /**
   * Platform setting `writeback_show_sap_row` — whether to show the ERP row key
   * column. Carried here so the tab needs only this one request.
   */
  show_sap_row?: boolean;
  summary: {
    pending: number;
    succeeded: number;
    failed: number;
    cancelled: number;
    /** Age of the oldest unsent writeback — the "is the queue moving" number. */
    oldest_pending_hours: number | null;
  };
  truncated: { pending: boolean; succeeded: boolean; failed: boolean; cancelled: boolean };
  pending: WritebackRow[];
  succeeded: WritebackRow[];
  failed: WritebackRow[];
  /** Withdrawn before collection. Kept so a cleared queue leaves a trace. */
  cancelled: WritebackRow[];
}

/** Current state of one PO line in Helix. */
export interface OrderLookupLine {
  line_number: number;
  part_number: string;
  description: string;
  quantity: number | string;
  received_qty: number | string;
  outstanding_qty: number | string;
  unit_price: number | string;
  // DATE-FIELD-CANON PR 3: canonical keys are the contract; the two below are
  // the deprecated aliases the API still sends for one release.
  customer_required_date: string | null;
  original_required_date: string | null;
  delivery_date: string | null;
  original_promise_date: string | null;
  supplier_promise_date: string | null;
  supplier_proposed_date: string | null;
  status: string;
  acknowledge_status: string;
  is_on_hold: boolean;
  /** Schedule slots (schedule-line tenants) — the grain people chase. */
  schedules: OrderLookupSchedule[];
}

/** One POLineSchedule slot with its recent audited ERP changes. */
export interface OrderLookupSchedule {
  schedule_line_number: string;
  schedule_date: string | null;
  schedule_qty: number | string;
  shipped_qty: number | string;
  balance_qty: number | string;
  status: string;
  acknowledge_status: string;
  on_hold: boolean;
  updated_at: string;
  changes: {
    field: string;
    old: string | null;
    new: string | null;
    reason: string | null;
    at: string;
  }[];
}

/** One ImportLog row — what the feed did to this order on one job. */
export interface OrderLookupImport {
  id: number;
  job_id: string;
  job_created_at: string | null;
  data_type: string;
  action: string;
  entity_reference: string;
  line_number: string;
  entity_type: string;
  source_row: number | null;
  error_code: string;
  error_message: string;
  fields_changed: Record<string, unknown> | null;
  /** null means the sampler did not retain the payload — not that none was sent. */
  raw_record: Record<string, unknown> | null;
}

export interface OrderLookupWriteback {
  id: number;
  line_number: string;
  change_type: number;
  previous_delivery_date: string | null;
  current_delivery_date: string | null;
  previous_unit_price: string | null;
  current_unit_price: string | null;
  previous_qty_ordered: string | null;
  current_qty_ordered: string | null;
  timestamp: string | null;
}

/** A field the import is currently refusing to update from the ERP feed. */
export interface OrderLookupHold {
  line_number: number;
  field: string;
  reason: string;
}

export interface OrderLookupResponse {
  order_no: string;
  /** Set when the search was narrowed to a single line. */
  line_no: string | null;
  customer_id: number;
  found: boolean;
  po: {
    id: number;
    po_number: string;
    supplier_name: string | null;
    status: string;
    currency: string;
    line_count: number;
  } | null;
  lines: OrderLookupLine[];
  held_fields: OrderLookupHold[];
  import_count: number;
  imports: OrderLookupImport[];
  imports_truncated: boolean;
  writebacks: {
    pending: OrderLookupWriteback[];
    succeeded: OrderLookupWriteback[];
    failed: OrderLookupWriteback[];
  };
}

/** One matching row from a retained push, on any queue. */
export interface QueueSearchRow {
  id: number;
  job_id: string;
  job_created_at: string | null;
  data_type: string;
  /** Redis queue the row arrived on. Blank on rows imported before this was stamped. */
  source_key: string;
  action: string;
  entity_type: string;
  entity_reference: string;
  source_row: number | null;
  error_code: string;
  error_message: string;
  fields_changed: Record<string, unknown> | null;
  raw_record: Record<string, unknown> | null;
}

export interface QueueSearchQueue {
  data_type: string;
  redis_keys: string[];
  retained_pushes: number;
  retained_rows: number;
  match_count: number;
  truncated: boolean;
  matches: QueueSearchRow[];
}

export interface QueueSearchResponse {
  q: string;
  customer_id: number;
  deep: boolean;
  deep_scanned: number;
  /** True when the payload scan stopped early — it has NOT looked everywhere. */
  deep_truncated: boolean;
  total_matches: number;
  queues: QueueSearchQueue[];
}

export interface ImportJobFilters {
  customer?: number;
  data_type?: string;
  source?: string;
  status?: string;
  created_after?: string;
  created_before?: string;
  has_failures?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
  /** 1 = paginate by push (correlation group) instead of by job; the
   * response then contains every job of the page's groups and `count`
   * is the number of groups. */
  group?: number;
}

// ============================================================================
// API
// ============================================================================

function jobsPrefix(source: ImportSource): string {
  switch (source) {
    case 'atlascopco': return '/imports/atlascopco-jobs/';
    case 'poeton': return '/imports/poeton-jobs/';
    case 'data_import': return '/imports/data-import-jobs/';
    default: return '/imports/jobs/';
  }
}

export interface DeliveryMetricsRecalcStatus {
  status: 'idle' | 'running' | 'completed' | 'failed';
  started_at?: string | null;
  finished_at?: string | null;
  result?: { updated?: number } | null;
  error?: string | null;
  triggered_by?: number | null;
  detail?: string;
}


/** One auto-cancelled schedule slot (ERP snapshot reconcile). */
export interface AutoCancelledRow {
  cancelled_at: string;
  po_number: string;
  line_number: number | null;
  schedule_line_number: string | null;
  schedule_date: string | null;
  part_number: string | null;
  schedule_qty: string | null;
  previous_status: string;
  current_status: string | null;
  reactivated: boolean;
  /** `closed` = receipts were linked, so the slot was closed with them kept rather than cancelled. */
  outcome: 'cancelled' | 'closed';
  /** Slot's acknowledge status now ('cancelled' / 'closed' while retired). */
  acknowledge_status: string | null;
  reason: string;
}

/** One schedule slot on collaboration hold (frozen against ERP imports). */
export interface CollabHoldRow {
  schedule_id: number;
  po_number: string;
  line_number: number | null;
  schedule_line_number: string | null;
  part_number: string | null;
  agreed_date: string | null;
  agreed_qty: string | null;
  erp_date: string | null;
  erp_last_seen_date: string | null;
  erp_last_seen_qty: string | null;
  erp_last_seen_at: string | null;
  held_since: string | null;
  held_days: number | null;
  accepted_by: string | null;
}

export interface CollabHoldsResponse {
  summary: {
    total: number;
    erp_attempted: number;
    never_seen_from_erp: number;
  };
  count: number;
  page: number;
  page_size: number;
  results: CollabHoldRow[];
}

export interface AutoCancelledResponse {
  summary: {
    total: number;
    still_cancelled: number;
    reactivated: number;
    /** Retired with receipts kept (acknowledge status 'closed') rather than cancelled. */
    closed: number;
    last_24h: number;
    window_days: number;
  };
  count: number;
  page: number;
  page_size: number;
  results: AutoCancelledRow[];
}

export const importsAPI = {
  getSources: () =>
    api.get<ImportSourceOption[]>('/imports/sources/').then(r => r.data),

  /** `customerId` narrows the summary to one customer in scope (CR-9030). */
  getDashboard: (source: ImportSource = 'pna', customerId?: number) =>
    api.get<DashboardSummary>('/imports/dashboard/', {
      params: customerId ? { source, customer_id: customerId } : { source },
    }).then(r => r.data),

  getJobs: (filters?: ImportJobFilters, source: ImportSource = 'pna') => {
    const prefix = jobsPrefix(source);
    return api.get<PaginatedResponse<ImportJob>>(prefix, { params: filters }).then(r => r.data);
  },

  getJob: (jobId: string, source: ImportSource = 'pna') => {
    const prefix = jobsPrefix(source);
    return api.get<ImportJobDetail>(`${prefix}${jobId}/`).then(r => r.data);
  },

  getJobLogs: (
    jobId: string,
    params?: { action?: string; entity_type?: string; error_code?: string; page?: number },
    source: ImportSource = 'pna',
  ) => {
    const prefix = jobsPrefix(source);
    return api.get<PaginatedResponse<ImportLog>>(`${prefix}${jobId}/logs/`, { params }).then(r => r.data);
  },

  triggerImport: (data: { data_type: string; customer_id: number; configuration_id?: number }) =>
    api.post<ImportJob>('/imports/jobs/', data).then(r => r.data),

  getConfigurations: (filters?: { customer?: number; data_type?: string; is_active?: boolean }) =>
    api.get<PaginatedResponse<ImportConfiguration>>('/imports/configurations/', { params: filters }).then(r => r.data),

  /**
   * Queue depths + middleware health; `customerId` narrows health to that
   * customer. A 503 (Redis down) still carries the health, so it resolves
   * with that body instead of failing.
   */
  getMiddlewareStatus: (source: ImportSource = 'pna', customerId?: number) =>
    api.get<MiddlewareStatusResponse>('/imports/middleware-status/', {
      params: customerId ? { source, customer_id: customerId } : { source },
    }).then(r => r.data).catch((err: unknown) => {
      const response = (err as { response?: { status?: number; data?: MiddlewareStatusResponse } }).response;
      if (response?.status === 503 && response.data) return response.data;
      throw err;
    }),

  /**
   * ERP schedule data-age (Avanti tenants only). `enabled: false` for every
   * other tenant — the Import Monitor renders nothing in that case.
   */
  getDataAge: (params?: { page?: number; page_size?: number; search?: string }) =>
    api.get<DataAgeResponse>('/imports/data-age/', { params }).then(r => r.data),


  /** Auto-cancelled slots — the ERP snapshot reconcile audit in one list. */
  getAutoCancelled: (
    customerId: number,
    opts?: { days?: number; search?: string; onlyStillCancelled?: boolean; page?: number; pageSize?: number },
  ) =>
    api.get<AutoCancelledResponse>('/imports/auto-cancelled/', {
      params: {
        customer_id: customerId,
        ...(opts?.days ? { days: opts.days } : {}),
        ...(opts?.search ? { search: opts.search } : {}),
        ...(opts?.onlyStillCancelled ? { only_still_cancelled: 1 } : {}),
        ...(opts?.page ? { page: opts.page } : {}),
        ...(opts?.pageSize ? { page_size: opts.pageSize } : {}),
      },
    }).then(r => r.data),

  /** Collaboration holds — accepted portal changes the ERP hasn't applied
   *  yet; these slots are frozen against ERP schedule imports. */
  getCollabHolds: (
    customerId: number,
    opts?: { search?: string; page?: number; pageSize?: number },
  ) =>
    api.get<CollabHoldsResponse>('/imports/collab-holds/', {
      params: {
        customer_id: customerId,
        ...(opts?.search ? { search: opts.search } : {}),
        ...(opts?.page ? { page: opts.page } : {}),
        ...(opts?.pageSize ? { page_size: opts.pageSize } : {}),
      },
    }).then(r => r.data),

  /** Everything the Import Monitor knows about one order: what the feed did to
   *  it, what it looks like now, and whether a writeback is holding a field. */
  getOrderLookup: (orderNo: string, customerId: number, lineNo?: string) =>
    api.get<OrderLookupResponse>('/imports/order-lookup/', {
      params: {
        order_no: orderNo,
        customer_id: customerId,
        ...(lineNo ? { line_no: lineNo } : {}),
      },
    }).then(r => r.data),

  /** Search the retained pushes (last 2 per data type) across every queue. */
  getQueueSearch: (
    q: string, customerId: number,
    opts?: { dataType?: string; deep?: boolean },
  ) =>
    api.get<QueueSearchResponse>('/imports/queue-search/', {
      params: {
        q, customer_id: customerId,
        ...(opts?.dataType ? { data_type: opts.dataType } : {}),
        ...(opts?.deep ? { deep: 1 } : {}),
      },
    }).then(r => r.data),

  /** Withdraw pending writebacks. Rows are kept with status=Cancelled, and the
   *  import hold on those fields is released. */
  cancelWritebacks: (
    customerId: number,
    opts: { ids?: number[]; changeType?: number; reason?: string },
  ) =>
    api.post<{ cancelled: number; ids: number[] }>('/imports/writeback-cancel/', {
      customer_id: customerId,
      ...(opts.ids ? { ids: opts.ids } : {}),
      ...(opts.changeType !== undefined ? { change_type: opts.changeType } : {}),
      ...(opts.reason ? { reason: opts.reason } : {}),
    }).then(r => r.data),

  getWritebackStatus: (customerId: number, hours = 48) =>
    api.get<WritebackStatusResponse>('/imports/writeback-status/', {
      params: { customer_id: customerId, hours },
    }).then(r => r.data),

  listHealthChecks: () =>
    api.get<{ checks: HealthCheckDescriptor[] }>('/imports/health-checks/').then(r => r.data.checks),

  runHealthCheck: (checkId: string, customerId: number) =>
    api.post<HealthCheckResponse>(
      `/imports/health-checks/${checkId}/run/`,
      { customer_id: customerId },
    ).then(r => r.data),

  cancelJob: (jobId: string) =>
    api.post<{ message: string; status: string }>(`/imports/jobs/${jobId}/cancel/`).then(r => r.data),

  // OTD + OTIF recalculation (admin, customer-wide)
  triggerDeliveryMetricsRecalc: () =>
    api.post<DeliveryMetricsRecalcStatus>('/imports/recalculate-delivery-metrics/').then(r => r.data),

  getDeliveryMetricsRecalcStatus: () =>
    api.get<DeliveryMetricsRecalcStatus>('/imports/recalculate-delivery-metrics/status/').then(r => r.data),
};
